import React, { ReactNode, useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useRouter } from 'next/router';
import styles from './MyAccount.module.scss';
import UpdatePassword, { UpdatePasswordHandle } from './UpdatePassword/UpdatePassword';
import PersonalInformation, {
  PersonalInformationHandle,
} from './PersonalInformation/PersonalInformation';
import { SvgIcon } from 'components/helpers/SvgIcon';
import LeavePageConfirmationDialog from './shared/LeavePageConfirmationDialog';
import CtaButton from 'components/helpers/Atoms/Cta/CtaButton';
import { EditableTabHandle } from './shared/EditableTabHandle';
import { signOut } from 'next-auth/react'; // ADDED: useSession
import { useIsMobileOrTablet } from 'lib/hooks/useIsMobile';
import useDictionary from 'lib/hooks/useDictionary';
import { useBlockNavigation } from 'lib/hooks/useBlockNavigation';
import EmailSubscription from './EmailSubscription/EmailSubscription';
import { SiteSettings } from 'lib/page-props-factory/plugins/site-settings';
import { DEFAULT_LOGIN_URL } from 'lib/utils/user-profile-urls';
import { useSessionSafe } from 'lib/hooks/auth/useSessionSafe';

// Stable module-level constants — no closure over component state.
const FOCUSABLE_SELECTORS =
  'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

const getFocusableChildren = (el: HTMLElement): HTMLElement[] =>
  [...el.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTORS)].filter(
    (c) => !c.closest('[aria-hidden="true"]')
  );

const focusIfVisible = (el: HTMLElement | null | undefined): boolean => {
  if (!el) return false;
  // Skip elements that are not actually visible (e.g., sidebar hidden on mobile)
  const style = window.getComputedStyle(el);
  if (style.display === 'none' || style.visibility === 'hidden') return false;
  if (el.getClientRects().length === 0) return false;

  try {
    el.focus();
  } catch {
    return false;
  }
  return document.activeElement === el;
};

export type TabId = 'password' | 'personal-information' | 'email-subscription';

export type TabDropdownOption = {
  label: string;
  value: string;
};

export interface TabFields {
  editPasswordTitle?: ReactNode;
  description?: ReactNode;
  editPasswordDescription?: ReactNode;
  currentPasswordLabel?: ReactNode;
  newPasswordLabel?: ReactNode;
  confirmPasswordLabel?: ReactNode;
  saveButtonText?: ReactNode;
  successMessage?: ReactNode;
  passwordNotSupportedText?: ReactNode;
  headline?: ReactNode;
  editHeadline?: ReactNode;
  editDescription?: ReactNode;
  emailLabel?: ReactNode;
  zipCodeLabel?: ReactNode;
  emailUpdateLabel?: ReactNode;
  zipCodeUpdateLabel?: ReactNode;
  questionLabel?: ReactNode;
  questionPlaceholder?: string;
  questionId?: string;
  questionOptions?: TabDropdownOption[];
  cancelButtonText?: ReactNode;
  sectionTitle?: ReactNode;
  sectionDescription?: ReactNode;
  errorMessage?: ReactNode;
  errorSecondaryMessage?: ReactNode;
  subscribeButtonText?: ReactNode;
  subscribeButtonAriaLabel?: string;
  unsubscribeButtonText?: ReactNode;
  unsubscribeButtonAriaLabel?: string;
  isSubscribed?: boolean;
  newsLetterSubscriptionId?: string;
  leavePageTitle?: ReactNode;
  leavePageDescription?: ReactNode;
  keepEditingButtonText?: string;
  leaveButtonText?: string;
}

export interface TabItem {
  id: TabId;
  labelNode: ReactNode | null;
  fields?: TabFields;
}

export interface MyAccountProps {
  headlineNode?: ReactNode | null;
  descriptionNode?: ReactNode | null;
  tabs?: TabItem[];
  activeTab?: TabId;
  externalLinks?: ReactNode[];
  logoutTextNode?: ReactNode | null;
  cta?: ReactNode | null;
  isPageEditing?: boolean;
  cardComponent?: ReactNode | null;
  siteSettings?: SiteSettings | null;
}

export default function MyAccount(props: MyAccountProps) {
  const {
    headlineNode,
    tabs,
    activeTab: initialActiveTab,
    externalLinks = [],
    logoutTextNode,
    cta,
    cardComponent,
    isPageEditing = false,
    siteSettings,
  } = props;

  const isMobile = useIsMobileOrTablet();
  const { getDictionaryValue } = useDictionary();

  // ADDED: read session status to guard against BFCache after logout
  const { status } = useSessionSafe(); // 'loading' | 'authenticated' | 'unauthenticated'

  const [activeTab, setActiveTab] = useState<TabId | null>(
    initialActiveTab || tabs?.[0]?.id || null
  );
  const [isEditMode, setIsEditMode] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [focusTabButtonId, setFocusTabButtonId] = useState<TabId | null>(null);

  const myAccountRef = useRef<HTMLDivElement>(null);
  const headingRef = useRef<HTMLDivElement>(null);
  const pendingNavigationRef = useRef<(() => void) | null>(null);
  const tabPanelRef = useRef<HTMLDivElement>(null);
  const backButtonRef = useRef<HTMLDivElement>(null);
  // Mutable map of tab id → button element for programmatic focus (no re-render needed)
  const tabButtonRefs = useRef<Partial<Record<TabId, HTMLButtonElement | null>>>({});
  // Tracks the last explicitly user-selected tab so resize (mobile↔desktop) never loses it
  const lastSelectedTabRef = useRef<TabId | null>(initialActiveTab || tabs?.[0]?.id || null);
  const externalLinksSectionRef = useRef<HTMLDivElement>(null);
  const ctaSectionRef = useRef<HTMLDivElement>(null);
  const cardComponentRef = useRef<HTMLDivElement>(null);
  const asideRef = useRef<HTMLElement>(null);
  const contentAreaRef = useRef<HTMLDivElement>(null);
  const shouldFocusPanelRef = useRef(false);
  // When we intentionally skip over the tab panel by moving focus out of the sidebar,
  // remember the target so Shift+Tab can return to the sidebar (reverse direction).
  const lastSidebarTabOutTargetRef = useRef<HTMLElement | null>(null);

  // Note: Each tab that supports edit mode should have a ref here
  const passwordRef = useRef<UpdatePasswordHandle>(null);
  const personalInformationRef = useRef<PersonalInformationHandle>(null);
  // const emailSubscriptionRef = useRef<EmailSubscriptionHandle>(null);

  const tabRefs: Record<TabId, React.RefObject<EditableTabHandle | null> | undefined> = useMemo(
    () => ({
      password: passwordRef,
      'personal-information': personalInformationRef,
      'email-subscription': undefined, // Add ref when implementing if needed
    }),
    []
  );

  const editableTabs = useMemo(() => new Set<TabId>(['password', 'personal-information']), []);

  const scrollToTop = () => {
    myAccountRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // ADDED: minimal client-side guard — if session is unauthenticated (e.g., after logout + Back), go to login
  useEffect(() => {
    if (!isPageEditing && status === 'unauthenticated') {
      window.location.assign(DEFAULT_LOGIN_URL);
    }
  }, [isPageEditing, status]);

  const router = useRouter();

  // WCAG 2.4.3 – Move focus to the My Account heading on mount and on same-page SPA re-navigation.
  useEffect(() => {
    const moveFocus = () => {
      // After a same-page route change Next.js does not unmount this component, so
      // myAccountRef.current being non-null confirms we are still on this page.
      if (isPageEditing || !myAccountRef.current) return;
      const target = headingRef.current ?? myAccountRef.current;
      if (!target) return;
      target.setAttribute('tabindex', '-1');
      // Suppress the visual focus ring on programmatic focus; screen reader still announces it.
      target.style.outline = 'none';
      target.focus();
      target.addEventListener(
        'blur',
        () => {
          target.removeAttribute('tabindex');
          target.style.outline = '';
        },
        { once: true }
      );
    };

    // Run on mount (covers external-page → My Account navigation).
    moveFocus();

    // Also run when a route change completes while this component is still mounted
    // (covers same-page re-navigation: already on My Account, clicks "Manage Account" again).
    router.events.on('routeChangeComplete', moveFocus);
    return () => router.events.off('routeChangeComplete', moveFocus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Check if current tab form is dirty
  const isFormDirty = useCallback(() => {
    if (!activeTab || !editableTabs.has(activeTab)) return false;
    return tabRefs[activeTab]?.current?.isDirty() ?? false;
  }, [activeTab, editableTabs, tabRefs]);

  const resetFormIfNeeded = useCallback(() => {
    if (activeTab && editableTabs.has(activeTab)) {
      tabRefs[activeTab]?.current?.resetForm();
    }
  }, [activeTab, editableTabs, tabRefs]);

  const exitEditMode = useCallback(() => {
    const tabToFocus = activeTab;
    resetFormIfNeeded();
    setIsEditMode(false);
    scrollToTop();

    if (tabToFocus) setFocusTabButtonId(tabToFocus);
  }, [activeTab, resetFormIfNeeded]);

  // Update activeTab based on viewport: clear on mobile/tablet, restore on desktop.
  // Uses lastSelectedTabRef so the user's tab selection survives resize events —
  // a functional prev-state update cannot be used here because a prior effect in the
  // same resize cycle may have already set activeTab to null.
  useEffect(() => {
    if (isMobile && !initialActiveTab) {
      setActiveTab(null);
    } else if (!isMobile && !initialActiveTab) {
      setActiveTab(lastSelectedTabRef.current);
    }
  }, [initialActiveTab, isMobile]);

  useEffect(() => {
    if (!shouldFocusPanelRef.current || !activeTab || isPageEditing) return;
    shouldFocusPanelRef.current = false;
    const panel = tabPanelRef.current;
    if (!panel) return;
    const firstFocusable = getFocusableChildren(panel)[0];
    if (focusIfVisible(firstFocusable)) return;

    panel.setAttribute('tabindex', '-1');
    panel.focus();
    panel.addEventListener('blur', () => panel.removeAttribute('tabindex'), { once: true });
  }, [activeTab, isPageEditing]);

  useEffect(() => {
    if (!focusTabButtonId) return;
    setFocusTabButtonId(null);
    tabButtonRefs.current[focusTabButtonId]?.focus();
  }, [focusTabButtonId]);

  const prevIsEditModeRef = useRef(false);

  useEffect(() => {
    if (isEditMode && !prevIsEditModeRef.current) {
      const btn = backButtonRef.current ? getFocusableChildren(backButtonRef.current)[0] : null;
      btn?.focus();
    }
    prevIsEditModeRef.current = isEditMode;
  }, [isEditMode]);

  // Block navigation when in edit mode AND form is dirty (handles routes, links)
  useBlockNavigation({
    isBlocking: isEditMode,
    isDirty: isFormDirty,
    onBlock: (navigate) => {
      setShowConfirmDialog(true);
      pendingNavigationRef.current = navigate;
    },
  });

  const handleEditModeChange = useCallback((editMode: boolean) => {
    setIsEditMode(editMode);
  }, []);

  // Preserve existing click behavior and reuse it for keyboard activation.
  // focusPanel: true for mouse clicks (focus moves into panel automatically),
  //             false for keyboard Enter/Space (focus stays on tab; user presses Tab to enter panel).
  const activateTab = useCallback(
    (tabId: TabId, focusPanel = true) => {
      // Record selection immediately so resize restores to this tab, not tabs[0]
      lastSelectedTabRef.current = tabId;
      // Only show confirmation if in edit mode AND form is dirty
      if (isEditMode && isFormDirty()) {
        setShowConfirmDialog(true);
        pendingNavigationRef.current = () => {
          setIsEditMode(false);
          shouldFocusPanelRef.current = focusPanel;
          setActiveTab(tabId);
        };
      } else {
        // If not dirty, just switch tabs (may still be in edit mode)
        if (isEditMode) {
          setIsEditMode(false);
        }
        shouldFocusPanelRef.current = focusPanel;
        setActiveTab(tabId);
      }
    },
    [isEditMode, isFormDirty]
  );

  // For this vertical tablist, arrow keys move focus between tabs without activating them.
  const moveFocusToTab = useCallback((tabId: TabId) => {
    tabButtonRefs.current[tabId]?.focus();
  }, []);

  const getTabIds = useCallback(() => tabs?.map((tab) => tab.id) ?? [], [tabs]);

  const moveFocusIntoActivePanel = useCallback(() => {
    window.requestAnimationFrame(() => {
      const panel = tabPanelRef.current;
      if (!panel) return;

      const firstFocusable = getFocusableChildren(panel)[0];
      if (focusIfVisible(firstFocusable)) return;

      panel.setAttribute('tabindex', '-1');
      panel.focus();
      panel.addEventListener('blur', () => panel.removeAttribute('tabindex'), { once: true });
    });
  }, []);

  const handleTabButtonKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLButtonElement>, tabId: TabId) => {
      const tabIds = getTabIds();
      const currentIndex = tabIds.indexOf(tabId);

      switch (e.key) {
        case 'ArrowDown':
          if (tabIds.length === 0) return;
          e.preventDefault();
          moveFocusToTab(tabIds[(currentIndex + 1) % tabIds.length]);
          return;
        case 'ArrowUp':
          if (tabIds.length === 0) return;
          e.preventDefault();
          moveFocusToTab(tabIds[(currentIndex - 1 + tabIds.length) % tabIds.length]);
          return;
        case 'Home':
          if (tabIds.length === 0) return;
          e.preventDefault();
          moveFocusToTab(tabIds[0]);
          return;
        case 'End':
          if (tabIds.length === 0) return;
          e.preventDefault();
          moveFocusToTab(tabIds[tabIds.length - 1]);
          return;
        case 'Enter':
        case ' ':
          e.preventDefault();
          activateTab(tabId, false); // keep focus on tab; user presses Tab to enter panel
          return;
        default:
          break;
      }

      if (e.key === 'Tab' && !e.shiftKey && tabId === activeTab) {
        e.preventDefault();
        moveFocusIntoActivePanel();
      }
    },
    [activeTab, activateTab, getTabIds, moveFocusIntoActivePanel, moveFocusToTab]
  );

  const handleSidebarLastTabKeyDown = useCallback((e: React.KeyboardEvent<HTMLElement>) => {
    if (e.key !== 'Tab') return;

    if (!e.shiftKey) {
      // Forward Tab: jump past the content panel when leaving the last sidebar element.
      const aside = asideRef.current;
      const contentArea = contentAreaRef.current;
      if (!aside || !contentArea) return;
      const asideFocusable = getFocusableChildren(aside);
      const lastAsideEl = asideFocusable[asideFocusable.length - 1];
      if (!lastAsideEl || e.target !== lastAsideEl) return;
      const contentFocusable = getFocusableChildren(contentArea);
      if (!contentFocusable.length) return;
      const bodyFocusable = getFocusableChildren(document.body);
      const lastContent = contentFocusable[contentFocusable.length - 1];
      const lastContentIdx = bodyFocusable.lastIndexOf(lastContent);
      const nextEl = lastContentIdx >= 0 ? bodyFocusable[lastContentIdx + 1] : null;
      if (nextEl) {
        e.preventDefault();
        lastSidebarTabOutTargetRef.current = nextEl;
        nextEl.focus();
      }
    } else {
      // Shift+Tab: if on the first element after the tablist (external links / logout),
      // redirect backward into the last focusable element of the active panel so the
      // reverse order mirrors the forward order.
      const firstFocusableIn = (ref: React.RefObject<HTMLDivElement | null>) =>
        ref.current ? (getFocusableChildren(ref.current)[0] ?? null) : null;
      const firstRedirectTarget =
        firstFocusableIn(externalLinksSectionRef) ??
        firstFocusableIn(ctaSectionRef) ??
        firstFocusableIn(cardComponentRef);
      if (!firstRedirectTarget || e.target !== firstRedirectTarget) return;
      const panel = tabPanelRef.current;
      if (!panel) return;
      const panelFocusable = getFocusableChildren(panel);
      const lastPanelEl = panelFocusable[panelFocusable.length - 1];
      if (lastPanelEl && focusIfVisible(lastPanelEl)) {
        e.preventDefault();
      }
    }
  }, []);

  // Reverse of handleSidebarLastTabKeyDown: when we Shift+Tab from the element that
  // we programmatically focused (to skip the tab panel), return focus to the last
  // focusable element in the sidebar.
  useEffect(() => {
    const onDocumentKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab' || !e.shiftKey) return;

      const tabOutTarget = lastSidebarTabOutTargetRef.current;
      if (!tabOutTarget) return;
      if (document.activeElement !== tabOutTarget) return;

      const aside = asideRef.current;
      if (!aside) return;

      const asideFocusable = getFocusableChildren(aside);
      const lastAsideEl = asideFocusable[asideFocusable.length - 1] ?? null;
      if (!lastAsideEl) return;

      e.preventDefault();
      lastSidebarTabOutTargetRef.current = null;
      lastAsideEl.focus();
    };

    document.addEventListener('keydown', onDocumentKeyDown);
    return () => document.removeEventListener('keydown', onDocumentKeyDown);
  }, []);

  const handleTabPanelKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key !== 'Tab') return;

      const panel = tabPanelRef.current;
      if (!panel) return;

      const focusable = getFocusableChildren(panel);
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const activeButton = activeTab ? tabButtonRefs.current[activeTab] : null;

      if (e.shiftKey && (!first || e.target === first)) {
        // In edit mode the back button is the logical predecessor; otherwise return to the active tab button
        e.preventDefault();
        if (isEditMode) {
          const btn = backButtonRef.current ? getFocusableChildren(backButtonRef.current)[0] : null;
          btn?.focus();
        } else {
          activeButton?.focus();
        }
        return;
      }

      if (!e.shiftKey && last && e.target === last) {
        // Skip unselected tab buttons — let focus flow to external links / logout / page.
        const firstFocusableIn = (ref: React.RefObject<HTMLDivElement | null>) =>
          ref.current ? (getFocusableChildren(ref.current)[0] ?? null) : null;
        const redirectTarget =
          firstFocusableIn(externalLinksSectionRef) ??
          firstFocusableIn(ctaSectionRef) ??
          firstFocusableIn(cardComponentRef);

        if (focusIfVisible(redirectTarget)) {
          e.preventDefault();
        }
      }
    },
    [activeTab, isEditMode]
  );

  const handleBackClick = () => {
    // Mobile: if not in edit mode, just go back to tab list (no dialog)
    if (isMobile && !isEditMode) {
      const prevTab = activeTab;
      setActiveTab(null);
      scrollToTop();
      if (prevTab) setFocusTabButtonId(prevTab);
      return;
    }

    // Edit mode: only show confirmation if form is dirty
    if (isEditMode && isFormDirty()) {
      setShowConfirmDialog(true);
      pendingNavigationRef.current = exitEditMode;
    } else {
      // Not dirty, just exit edit mode
      exitEditMode();
    }
  };

  const handleRequestCancel = useCallback(() => {
    // Only show dialog if form is dirty
    if (isFormDirty()) {
      setShowConfirmDialog(true);
      pendingNavigationRef.current = exitEditMode;
    } else {
      exitEditMode();
    }
  }, [isFormDirty, exitEditMode]);

  const handleConfirmLeave = () => {
    const tabToFocus = activeTab;
    resetFormIfNeeded();
    setIsEditMode(false);
    setShowConfirmDialog(false);

    // Execute pending navigation if any
    if (pendingNavigationRef.current) {
      const navigation = pendingNavigationRef.current;
      pendingNavigationRef.current = null;
      navigation();
    } else {
      if (tabToFocus) setFocusTabButtonId(tabToFocus);
    }
    scrollToTop();
  };

  const handleCancelLeave = () => {
    setShowConfirmDialog(false);
    pendingNavigationRef.current = null;
    scrollToTop();
  };

  const renderTabPanel = (tabId: TabId) => {
    const tab = tabs?.find((t) => t.id === tabId);

    switch (tabId) {
      case 'password':
        return (
          <UpdatePassword
            ref={passwordRef}
            tabLabelNode={tab?.labelNode}
            headlineNode={tab?.fields?.editPasswordTitle}
            descriptionNode={tab?.fields?.description}
            editDescriptionNode={tab?.fields?.editPasswordDescription}
            currentPasswordLabelNode={tab?.fields?.currentPasswordLabel}
            newPasswordLabelNode={tab?.fields?.newPasswordLabel}
            confirmPasswordLabelNode={tab?.fields?.confirmPasswordLabel}
            saveButtonTextNode={tab?.fields?.saveButtonText}
            successMessageNode={tab?.fields?.successMessage}
            passwordNotSupportedText={tab?.fields?.passwordNotSupportedText}
            isEditMode={isEditMode}
            onEditModeChange={handleEditModeChange}
            onRequestCancel={handleRequestCancel}
          />
        );
      case 'personal-information':
        return (
          <PersonalInformation
            ref={personalInformationRef}
            tabLabelNode={tab?.labelNode}
            headlineNode={tab?.fields?.headline}
            descriptionNode={tab?.fields?.description}
            editHeadlineNode={tab?.fields?.editHeadline}
            editDescriptionNode={tab?.fields?.editDescription}
            emailLabelNode={tab?.fields?.emailLabel}
            zipCodeLabelNode={tab?.fields?.zipCodeLabel}
            emailUpdateLabelNode={tab?.fields?.emailUpdateLabel}
            zipCodeUpdateLabelNode={tab?.fields?.zipCodeUpdateLabel}
            questionLabelNode={tab?.fields?.questionLabel as ReactNode | undefined}
            questionPlaceholder={
              typeof tab?.fields?.questionPlaceholder === 'string'
                ? tab.fields.questionPlaceholder
                : undefined
            }
            questionId={
              typeof tab?.fields?.questionId === 'string' ? tab.fields.questionId : undefined
            }
            questionOptions={
              Array.isArray(tab?.fields?.questionOptions)
                ? (tab.fields.questionOptions as { label: string; value: string }[])
                : undefined
            }
            saveButtonTextNode={tab?.fields?.saveButtonText}
            cancelButtonTextNode={tab?.fields?.cancelButtonText}
            successMessageNode={tab?.fields?.successMessage}
            isEditMode={isEditMode}
            onEditModeChange={handleEditModeChange}
            onRequestCancel={handleRequestCancel}
          />
        );
      case 'email-subscription':
        return (
          <EmailSubscription
            title={tab?.fields?.sectionTitle}
            sectionDescription={tab?.fields?.sectionDescription}
            description={tab?.fields?.description}
            errorMessage={tab?.fields?.errorMessage}
            errorSecondaryMessage={tab?.fields?.errorSecondaryMessage}
            subscribeButtonText={tab?.fields?.subscribeButtonText}
            subscribeButtonAriaLabel={tab?.fields?.subscribeButtonAriaLabel as string | undefined}
            successMessage={tab?.fields?.successMessage}
            unsubscribeButtonText={tab?.fields?.unsubscribeButtonText}
            unsubscribeButtonAriaLabel={
              tab?.fields?.unsubscribeButtonAriaLabel as string | undefined
            }
            newsLetterSubscriptionId={siteSettings?.subscriptionID?.value}
            isSubscribedEmailSubscription={!!tab?.fields?.isSubscribed}
          />
        );
      default:
        return null;
    }
  };

  const renderContent = () => {
    if (isPageEditing) {
      // isPageEditing mode: Render all panels, hide inactive with display:none
      // This ensures all content is available for authors to edit
      return (
        <>
          {tabs?.map((tab) => (
            <div
              key={tab.id}
              role="tabpanel"
              id={`tabpanel-${tab.id}`}
              aria-labelledby={`tab-${tab.id}`}
              aria-hidden={activeTab !== tab.id}
              style={{ display: activeTab === tab.id ? 'block' : 'none' }}
              className={styles.tabPanel}
            >
              {renderTabPanel(tab.id)}
            </div>
          ))}
        </>
      );
    }

    // Normal mode: Conditional rendering (single active panel)
    return activeTab ? renderTabPanel(activeTab) : null;
  };

  const activeTabFields = useMemo(
    () => tabs?.find((tab) => tab.id === activeTab)?.fields,
    [tabs, activeTab]
  );

  return (
    <div
      className={styles.myAccountContainer}
      ref={myAccountRef}
      data-edit-mode={String(isEditMode)}
    >
      {/* Show back button in edit mode (desktop/mobile) or when tab is selected in mobile */}
      {/* CSS handles mobile visibility - always render both, CSS shows/hides */}
      <div
        className={styles.headerBackButton}
        data-show-back-button={String(isEditMode || activeTab !== null)}
        ref={backButtonRef}
      >
        <CtaButton
          ctaStyle="link"
          ctaVariant="brand"
          ctaIconBefore="arrow-left"
          onClick={handleBackClick}
        >
          {getDictionaryValue('Components.MyAccount.Back', 'Back')}
        </CtaButton>
      </div>
      {headlineNode && (
        <div
          className={styles.headerTitle}
          data-hide-title={String(isEditMode || activeTab !== null)}
          ref={headingRef}
        >
          {headlineNode}
        </div>
      )}

      <div className={styles.contentWrapper}>
        {/* Always render sidebar - CSS handles visibility based on mobile/desktop and state */}
        <aside
          className={styles.sidebar}
          data-hide-mobile-sidebar={String(isEditMode || activeTab !== null)}
          ref={asideRef}
          onKeyDown={handleSidebarLastTabKeyDown}
        >
          <div className={styles.sidebarAction}>
            <nav
              className={styles.sidebarNav}
              aria-label={getDictionaryValue(
                'Components.MyAccount.AccountSettings',
                'Account settings'
              )}
            >
              <ul className={styles.sidebarList} role="tablist" aria-orientation="vertical">
                {tabs?.map((tab) => (
                  <li key={tab.id} className={styles.sidebarItem} role="presentation">
                    <button
                      type="button"
                      role="tab"
                      aria-selected={activeTab === tab.id}
                      aria-controls={`tabpanel-${tab.id}`}
                      id={`tab-${tab.id}`}
                      tabIndex={activeTab === tab.id ? 0 : -1}
                      className={styles.sidebarButton}
                      onClick={() => activateTab(tab.id)}
                      ref={(el) => {
                        tabButtonRefs.current[tab.id] = el;
                      }}
                      onKeyDown={(e) => handleTabButtonKeyDown(e, tab.id)}
                    >
                      {tab.labelNode && (
                        <>
                          <span className={styles.sidebarButtonText}>{tab.labelNode}</span>
                          <SvgIcon
                            icon="chevron-right"
                            size="sm"
                            className={styles.sidebarButtonIcon}
                          />
                        </>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            </nav>

            {(externalLinks.length > 0 || logoutTextNode) && (
              <div className={styles.externalLinksSection} ref={externalLinksSectionRef}>
                {externalLinks.length > 0 &&
                  externalLinks.map((link, _index) => (
                    <React.Fragment key={_index}>{link}</React.Fragment>
                  ))}
                {logoutTextNode && (
                  <CtaButton
                    ctaStyle="link"
                    ctaVariant="brand"
                    ctaIconAfter="logout"
                    ctaIconSize="sm"
                    onClick={() => {
                      signOut({ callbackUrl: DEFAULT_LOGIN_URL });
                    }}
                  >
                    {logoutTextNode}
                  </CtaButton>
                )}
              </div>
            )}
          </div>

          {cta && (
            <div className={styles.ctaSection} ref={ctaSectionRef}>
              {cta}
            </div>
          )}

          {cardComponent && (
            <div className={styles.cardComponent} ref={cardComponentRef}>
              {cardComponent}
            </div>
          )}
        </aside>

        <div className={styles.contentArea} ref={contentAreaRef}>
          {isPageEditing ? (
            // Sitecore editing mode: panels have their own tabpanel role/attributes
            renderContent()
          ) : activeTab ? (
            // CMSMGR-12639 / CMSMGR-19456: NO role="tabpanel" — NVDA maps it to "property page" breaking button announcements; aria-controls on tab buttons preserves the relationship.
            <div
              id={`tabpanel-${activeTab}`}
              className={styles.tabPanel}
              ref={tabPanelRef}
              onKeyDown={handleTabPanelKeyDown}
            >
              {renderContent()}
            </div>
          ) : null}
        </div>
      </div>

      {(isPageEditing || showConfirmDialog) && (
        <LeavePageConfirmationDialog
          isOpen={showConfirmDialog}
          onCancel={handleCancelLeave}
          onConfirm={handleConfirmLeave}
          title={activeTabFields?.leavePageTitle}
          message={activeTabFields?.leavePageDescription}
          leaveButtonText={activeTabFields?.leaveButtonText as string | undefined}
          keepEditingButtonText={activeTabFields?.keepEditingButtonText as string | undefined}
        />
      )}
    </div>
  );
}
